// FX API Service for Silk Road Terminal
// Fetches exchange rates from free APIs

import { cacheService, FX_TTL } from '../data/cacheService';
import type { APIResponse, FXRate, FXHistory } from './types';

// Using exchangerate.host API (free, no key required)
const FX_BASE_URL = 'https://api.exchangerate.host';
// Fallback: frankfurter.app (also free)
const FX_FALLBACK_URL = 'https://api.frankfurter.app';

const MAX_RETRIES = 2;

export interface FXRateResponse {
  base: string;
  target: string;
  rate: number;
  change24h: number | null;
  changePercent24h: number | null;
  timestamp: Date;
  source: 'live' | 'cached' | 'static';
}

export interface FXHistoryResponse {
  base: string;
  target: string;
  data: FXHistory[];
  source: 'live' | 'cached' | 'static';
}

/**
 * Static reference rates for UZS and other Central Asian currencies
 * These are used ONLY as fallback reference values when APIs are unavailable
 * Source: Central Bank official rates as of December 2024
 * Last updated: 2024-12-15
 *
 * IMPORTANT: These should be periodically updated from official central bank sources
 */
const STATIC_RATES: Record<string, Record<string, number>> = {
  USD: {
    UZS: 12850,  // Central Bank of Uzbekistan
    KZT: 450,    // National Bank of Kazakhstan
    GEL: 2.65,   // National Bank of Georgia
    VND: 24500,  // State Bank of Vietnam
    RWF: 1280,   // National Bank of Rwanda
    BDT: 110,    // Bangladesh Bank
    MNT: 3450,   // Bank of Mongolia
    RSD: 108,    // National Bank of Serbia
    EUR: 0.92,   // European Central Bank
    GBP: 0.79,   // Bank of England
  },
};

/**
 * Fetch current exchange rate
 */
export const fetchRate = async (
  base: string,
  target: string
): Promise<APIResponse<FXRateResponse>> => {
  const cacheKey = `fx_rate_${base}_${target}`;

  // Check cache
  const cached = cacheService.getStale<FXRateResponse>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(cached.data.timestamp),
      isStale: false,
    };
  }

  try {
    // Try primary API
    const url = `${FX_BASE_URL}/latest?base=${base}&symbols=${target}`;
    const response = await fetch(url);

    if (response.ok) {
      const data = await response.json();

      if (data.rates && data.rates[target]) {
        const rate: FXRateResponse = {
          base,
          target,
          rate: data.rates[target],
          change24h: null, // Would need historical data
          changePercent24h: null,
          timestamp: new Date(),
          source: 'live',
        };

        cacheService.set(cacheKey, rate, FX_TTL);

        return {
          data: rate,
          error: null,
          source: 'live',
          lastUpdated: new Date(),
          isStale: false,
        };
      }
    }

    // Try fallback API
    const fallbackUrl = `${FX_FALLBACK_URL}/latest?from=${base}&to=${target}`;
    const fallbackResponse = await fetch(fallbackUrl);

    if (fallbackResponse.ok) {
      const fallbackData = await fallbackResponse.json();

      if (fallbackData.rates && fallbackData.rates[target]) {
        const rate: FXRateResponse = {
          base,
          target,
          rate: fallbackData.rates[target],
          change24h: null,
          changePercent24h: null,
          timestamp: new Date(),
          source: 'live',
        };

        cacheService.set(cacheKey, rate, FX_TTL);

        return {
          data: rate,
          error: null,
          source: 'live',
          lastUpdated: new Date(),
          isStale: false,
        };
      }
    }

    throw new Error('Failed to fetch from all FX APIs');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    // Return cached data if available
    if (cached.data) {
      return {
        data: cached.data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(cached.data.timestamp),
        isStale: true,
      };
    }

    // Use static fallback
    const staticRate = STATIC_RATES[base]?.[target];
    if (staticRate) {
      const rate: FXRateResponse = {
        base,
        target,
        rate: staticRate,
        change24h: null,
        changePercent24h: null,
        timestamp: new Date(),
        source: 'static',
      };

      return {
        data: rate,
        error: `Using static rate: ${errorMessage}`,
        source: 'static',
        lastUpdated: null,
        isStale: true,
      };
    }

    return {
      data: null,
      error: errorMessage,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Fetch historical exchange rates
 */
export const fetchHistory = async (
  base: string,
  target: string,
  days: number = 30
): Promise<APIResponse<FXHistoryResponse>> => {
  const cacheKey = `fx_history_${base}_${target}_${days}`;

  // Check cache
  const cached = cacheService.getStale<FXHistoryResponse>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];

    // Try primary API
    const url = `${FX_BASE_URL}/timeseries?start_date=${startStr}&end_date=${endStr}&base=${base}&symbols=${target}`;
    const response = await fetch(url);

    if (response.ok) {
      const data = await response.json();

      if (data.rates) {
        const history: FXHistory[] = Object.entries(data.rates)
          .map(([date, rates]: [string, unknown]) => ({
            date,
            rate: (rates as Record<string, number>)[target] || 0,
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        const result: FXHistoryResponse = {
          base,
          target,
          data: history,
          source: 'live',
        };

        cacheService.set(cacheKey, result, FX_TTL * 2); // Cache history longer

        return {
          data: result,
          error: null,
          source: 'live',
          lastUpdated: new Date(),
          isStale: false,
        };
      }
    }

    // Try fallback API
    const fallbackUrl = `${FX_FALLBACK_URL}/${startStr}..${endStr}?from=${base}&to=${target}`;
    const fallbackResponse = await fetch(fallbackUrl);

    if (fallbackResponse.ok) {
      const fallbackData = await fallbackResponse.json();

      if (fallbackData.rates) {
        const history: FXHistory[] = Object.entries(fallbackData.rates)
          .map(([date, rates]: [string, unknown]) => ({
            date,
            rate: (rates as Record<string, number>)[target] || 0,
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        const result: FXHistoryResponse = {
          base,
          target,
          data: history,
          source: 'live',
        };

        cacheService.set(cacheKey, result, FX_TTL * 2);

        return {
          data: result,
          error: null,
          source: 'live',
          lastUpdated: new Date(),
          isStale: false,
        };
      }
    }

    throw new Error('Failed to fetch FX history from all APIs');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (cached.data) {
      return {
        data: cached.data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    // Do NOT generate fake historical data with random variance
    // If we have a static rate, return it as a single-point reference only
    const staticRate = STATIC_RATES[base]?.[target];
    if (staticRate) {
      // Return only today's rate as a reference point - NOT fake historical data
      const today = new Date().toISOString().split('T')[0];
      const result: FXHistoryResponse = {
        base,
        target,
        data: [{ date: today, rate: staticRate }], // Single reference point only
        source: 'static',
      };

      return {
        data: result,
        error: `Historical data unavailable. Showing reference rate only: ${errorMessage}`,
        source: 'static',
        lastUpdated: null,
        isStale: true,
      };
    }

    // No data available
    return {
      data: null,
      error: `Historical FX data unavailable: ${errorMessage}`,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Fetch multiple currency rates at once
 */
export const fetchMultipleRates = async (
  base: string,
  targets: string[]
): Promise<APIResponse<Record<string, number>>> => {
  const cacheKey = `fx_multi_${base}_${targets.join('_')}`;

  // Check cache
  const cached = cacheService.getStale<Record<string, number>>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  try {
    const url = `${FX_BASE_URL}/latest?base=${base}&symbols=${targets.join(',')}`;
    const response = await fetch(url);

    if (response.ok) {
      const data = await response.json();

      if (data.rates) {
        const rates: Record<string, number> = {};
        for (const target of targets) {
          rates[target] = data.rates[target] || STATIC_RATES[base]?.[target] || 0;
        }

        cacheService.set(cacheKey, rates, FX_TTL);

        return {
          data: rates,
          error: null,
          source: 'live',
          lastUpdated: new Date(),
          isStale: false,
        };
      }
    }

    throw new Error('Failed to fetch multiple rates');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (cached.data) {
      return {
        data: cached.data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    // Use static rates
    const rates: Record<string, number> = {};
    for (const target of targets) {
      rates[target] = STATIC_RATES[base]?.[target] || 0;
    }

    return {
      data: rates,
      error: `Using static rates: ${errorMessage}`,
      source: 'static',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Convert amount between currencies
 */
export const convertAmount = async (
  amount: number,
  from: string,
  to: string
): Promise<{ converted: number; rate: number } | null> => {
  const response = await fetchRate(from, to);

  if (response.data) {
    return {
      converted: amount * response.data.rate,
      rate: response.data.rate,
    };
  }

  return null;
};

/**
 * Check if FX API is accessible
 */
export const checkAPIHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${FX_BASE_URL}/latest?base=USD&symbols=EUR`);
    return response.ok;
  } catch {
    return false;
  }
};

// Export default object
const fxApi = {
  fetchRate,
  fetchHistory,
  fetchMultipleRates,
  convertAmount,
  checkAPIHealth,
  STATIC_RATES,
};

export default fxApi;
